import {
  AddMilestoneFromListModal,
  ManageProjectScheduleModal,
} from "@/containers/Project/components";
import { PassesProjectCompleteRequirements } from "@/containers/Project/helper";
import { PROJECT_STATUS, ProjectMilestoneStatus, RIGHTS } from "@/enums";
import ProjectUserPermissions from "@/enums/ProjectUserPermissions";
import { getMilestoneNotificationAdGroupIds } from "@/helpers";
import {
  useChangeMilestoneStatusMutation,
  useDeleteProjectMilestoneMutation,
  useGetProjectQuery,
  useGetTemplateRolesMappingQuery,
  useLazyGetTestPlansQuery,
  useNotifyMilestoneApproversMutation,
  useUpdateProjectMilestoneMutation,
} from "@/services";
import { applicationErrorActions } from "@/store";
import { Spin } from "antd";
import _ from "lodash";
import { node } from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useUserContext } from "./UserProvider";
import {
  calculateCanEditMilestoneActualDate,
  calculateCanEditMilestoneTargetDates,
  calculateCanUserModifyProjectStatus,
  calculateIsUserMilestoneEditor,
  calculateIsUserProjectLeader,
  calculateIsUserProjectTeamMember,
} from "./helpers";

export const ProjectContext = createContext();
export const useProjectContext = () => useContext(ProjectContext);

export const ProjectProvider = ({ children }) => {
  const { projectId } = useParams();
  const { userToken, hasRight } = useUserContext();
  const intl = useIntl();
  const dispatch = useDispatch();

  const [parentMilestone, setParentMilestone] = useState(null);
  const [addMilestoneModalOpen, setAddMilestoneModalOpen] = useState(false);
  const [manageSchedulesModalOpen, setManageSchedulesModalOpen] =
    useState(false);
  const [expandedMilestone, setExpandedMilestone] = useState(null);
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [editMilestone, setEditMilestone] = useState(null);
  const [
    milestoneStatusActionCommentModal,
    setMilestoneStatusActionCommentModal,
  ] = useState({ milestoneId: null, targetStatus: null, finalApprover: false });
  const [milestoneListCollapsed, setMilestoneListCollapsed] = useReducer(
    (state) => !state,
    true
  );
  const [milestoneListChildren, setMilestoneListChildren] = useState({
    topOnly: true,
    showOrHideChildren: [],
  });
  const [project, setProject] = useState();
  const [templateRolesMap, setTemplateRolesMap] = useState();

  const {
    data: getProjectData,
    refetch: refetchProject,
    isLoading: projectLoading,
    isError: isProjectError,
    error,
  } = useGetProjectQuery(projectId, {
    skip: !projectId,
  });

  const {
    data: getTemplateRolesMapping,
    isLoading: templateRolesMapLoading,
    isError: isTemplateRolesMapError,
    refetch: refetchTemplateRolesMapping,
  } = useGetTemplateRolesMappingQuery(projectId, {
    skip: !projectId,
  });

  const [updateProjectMilestone, { isLoading: updatingMilestone }] =
    useUpdateProjectMilestoneMutation();
  const [deleteProjectMilestone] = useDeleteProjectMilestoneMutation({
    fixedCacheKey: "delete-project-milestone",
  });
  const [changeMilestoneStatus, { isLoading: updatingMilestoneStatus }] =
    useChangeMilestoneStatusMutation();
  const [notifyMilestoneApprovers] = useNotifyMilestoneApproversMutation();
  const [
    triggerGetCtqTestPlans,
    {
      data: ctqTestPlansData,
      isFetching: ctqTestPlansLoading,
      isSuccess: ctqTestPlansQuerySuccess,
      originalArgs: ctqTestPlansArgs,
    },
  ] = useLazyGetTestPlansQuery();
  const ctqTestPlansSuccess =
    ctqTestPlansQuerySuccess &&
    ctqTestPlansArgs?.routeParams?.projectId === projectId;
  const ctqTestPlans = ctqTestPlansSuccess ? ctqTestPlansData : undefined;

  useEffect(() => {
    setTemplateRolesMap(getTemplateRolesMapping);
  }, [getTemplateRolesMapping]);

  useEffect(() => {
    if (!isProjectError && !isTemplateRolesMapError) return;
    dispatch(applicationErrorActions.setApplicationError({ response: error }));
  });

  useEffect(() => {
    setProject(getProjectData);
  }, [getProjectData]);

  useEffect(() => {
    if (milestoneListCollapsed) {
      setExpandedMilestone(null);
    }
  }, [milestoneListCollapsed]);

  const toggleTopOnly = () => {
    setMilestoneListChildren({
      topOnly: !milestoneListChildren.topOnly,
      showOrHideChildren: [],
    });
  };

  const addTopLevelMilestoneToShowOrHideChildren = (milestoneId) => {
    setMilestoneListChildren((value) => ({
      ...value,
      showOrHideChildren: [
        ...new Set([...value.showOrHideChildren, milestoneId]),
      ],
    }));
  };

  const removeTopLevelMilestoneFromShowOrHideChildren = (milestoneId) => {
    const removed = milestoneListChildren.showOrHideChildren.filter(
      (id) => id !== milestoneId
    );
    setMilestoneListChildren({
      ...milestoneListChildren,
      showOrHideChildren: removed,
    });
  };

  const openMilestoneModal = (parent) => {
    setParentMilestone(parent);
    setAddMilestoneModalOpen(true);
  };

  const closeMilestoneModal = () => {
    setParentMilestone(null);
    setAddMilestoneModalOpen(false);
  };

  const isProjectComplete = project?.isProjectComplete;

  const isUserProjectTeamViewer = useCallback(() => {
    const canViewProject = !!project.projectReadOnlyUsers?.includes(
      userToken?.user?.id
    );
    return canViewProject;
  }, [project?.projectReadOnlyUsers, userToken?.user?.id]);

  const isUserProjectTeamMember = useCallback(
    () => calculateIsUserProjectTeamMember(projectLoading, project, userToken),
    [projectLoading, project, userToken]
  );

  const isUserMilestoneEditor = useCallback(
    () => calculateIsUserMilestoneEditor(projectLoading, project, userToken),
    [projectLoading, project, userToken]
  );

  const isUserProjectLeader = useCallback(
    () => calculateIsUserProjectLeader(projectLoading, project, userToken),
    [project, projectLoading, userToken]
  );

  const canUserModifyProject = useCallback(
    () =>
      (isUserProjectLeader() || hasRight([RIGHTS.writeAllProjects])) &&
      calculateCanUserModifyProjectStatus(project?.status),
    [isUserProjectLeader, project?.status]
  );

  const isUserNotificationUser = useCallback(() => {
    if (!projectLoading && project && userToken?.azureActiveDirectoryGroupIds) {
      let totalAzAdGroupsIds = [];

      for (const schedule of project.schedules) {
        totalAzAdGroupsIds = [
          ...totalAzAdGroupsIds,
          ...getMilestoneNotificationAdGroupIds(schedule.milestones),
        ];
      }

      return totalAzAdGroupsIds.some((g) =>
        (userToken.AzureActiveDirectoryGroupIds || []).includes(g)
      );
    }
  }, [project, projectLoading, userToken]);

  const isUserMilestoneApprover = useCallback(
    (milestone) => {
      const approverIds = milestone.approvers
        ?.map((approver) => approver.assignedUsers?.map((u) => u.userId))
        .flat();

      if (
        _.uniq(approverIds)?.find(
          (approverId) => approverId === userToken?.user?.id
        )
      ) {
        return true;
      }
      return false;
    },
    [userToken]
  );

  const canUser = useCallback(
    (action, milestone) => {
      switch (action) {
        case ProjectUserPermissions.SubmitAndReleaseMilestones:
          return (
            (canUserModifyProject() || isUserMilestoneEditor()) &&
            !isProjectComplete
          );
        case ProjectUserPermissions.EditProjectLeaders:
        case ProjectUserPermissions.EditSchedules:
        case ProjectUserPermissions.EditProjectImages:
        case ProjectUserPermissions.EditProjectSkus:
        case ProjectUserPermissions.EditStatusKpis:
        case ProjectUserPermissions.EditProjectRoles:
        case ProjectUserPermissions.ViewScheduleDropdown:
          return (
            (hasRight([RIGHTS.writeAllProjects]) ||
              isUserProjectLeader() ||
              isUserMilestoneEditor()) &&
            !isProjectComplete
          );

        case ProjectUserPermissions.ResetCompleteMilestones:
          return hasRight([RIGHTS.resetProjectMilestoneStatus]);

        case ProjectUserPermissions.AddMilestones:
          return canUserModifyProject() && !isProjectComplete;

        case ProjectUserPermissions.EditMilestoneTargetDates:
          return calculateCanEditMilestoneTargetDates(
            milestone,
            canUserModifyProject(),
            isProjectComplete
          );

        case ProjectUserPermissions.EditMilestoneActualDates:
          return calculateCanEditMilestoneActualDate(
            milestone,
            canUserModifyProject(),
            isProjectComplete
          );

        case ProjectUserPermissions.EditApprovers:
        case ProjectUserPermissions.EditNotified:
          return (
            (canUserModifyProject() || isUserMilestoneEditor()) &&
            (milestone.status === ProjectMilestoneStatus.Draft ||
              milestone.status === ProjectMilestoneStatus.Rejected) &&
            !isProjectComplete
          );

        case ProjectUserPermissions.EditMilestoneDocument:
          return (
            (canUserModifyProject() ||
              isUserProjectTeamMember() ||
              isUserMilestoneEditor()) &&
            !isProjectComplete &&
            milestone.status !== ProjectMilestoneStatus.Approved &&
            milestone.status !== ProjectMilestoneStatus.Released &&
            milestone.status !== ProjectMilestoneStatus.InApproval &&
            milestone.status !== ProjectMilestoneStatus.Skipped
          );
        case ProjectUserPermissions.ViewMilestoneDropdownOptions:
          return (
            canUserModifyProject() &&
            milestone.status !== ProjectMilestoneStatus.Approved &&
            milestone.status !== ProjectMilestoneStatus.Released &&
            milestone.status !== ProjectMilestoneStatus.InApproval &&
            !isProjectComplete
          );
        case ProjectUserPermissions.EditMilestoneComments:
          return (
            (canUserModifyProject() ||
              isUserProjectTeamMember() ||
              isUserMilestoneApprover(milestone) ||
              isUserProjectTeamViewer() ||
              isUserNotificationUser()) &&
            milestone.status !== ProjectMilestoneStatus.Approved &&
            milestone.status !== ProjectMilestoneStatus.Released &&
            milestone.status !== ProjectMilestoneStatus.InApproval &&
            milestone.status !== ProjectMilestoneStatus.Skipped
          );
        case ProjectUserPermissions.EditCompletedProject:
          return hasRight([RIGHTS.editCompletedProject]);
        default:
          return false;
      }
    },
    [
      hasRight,
      isUserProjectLeader,
      canUserModifyProject,
      isUserMilestoneEditor,
      isProjectComplete,
      isUserProjectTeamMember,
      isUserMilestoneApprover,
      isUserProjectTeamViewer,
      isUserNotificationUser,
    ]
  );

  const canProjectBeCompleted = () =>
    project && PassesProjectCompleteRequirements(project);

  const updateMilestone = (milestone) => {
    updateProjectMilestone({ projectId: project.id, milestone })
      .unwrap()
      .then((project) => {
        toast.success(intl.formatMessage({ id: "editMilestonesModal.saved" }));
        setProject(project);
        refetchTemplateRolesMapping();
      })
      .catch(() => {
        toast.error(intl.formatMessage({ id: "editMilestonesModal.error" }));
      });
  };

  const deleteMilestone = (milestoneId) => {
    deleteProjectMilestone({ projectId: project.id, milestoneId })
      .unwrap()
      .then((project) => {
        toast.success(
          intl.formatMessage({ id: "milestonesList.milestoneDeleted" })
        );
        setProject(project);
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({ id: "milestonesList.milestoneDeletedError" })
        );
      });
  };

  const milestoneStatusChange = (
    projectId,
    milestoneId,
    statusAction,
    comment,
    form
  ) => {
    changeMilestoneStatus({
      projectId,
      milestoneId,
      statusAction,
      comment,
    })
      .unwrap()
      .then((project) => {
        toast.success(
          intl.formatMessage({ id: "milestonesList.statusChangeSuccess" })
        );
        setMilestoneStatusActionCommentModal({
          milestoneId: null,
          targetStatus: null,
          finalApprover: false,
        });
        if (form) {
          form.resetFields();
        }
        setProject(project.data);
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({ id: "milestonesList.statusChangeError" })
        );
      });
  };

  const requestCtqTestPlans = () => {
    if (ctqTestPlansLoading || ctqTestPlansSuccess) {
      return;
    }
    triggerGetCtqTestPlans({ routeParams: { projectId: project.id } });
  };

  const handleNotifyApprovers = (projectId, milestoneId, approversToNotify) => {
    notifyMilestoneApprovers({ projectId, milestoneId, approversToNotify })
      .unwrap()
      .then((response) => {
        console.log("Notification sent:", response);
      })
      .catch((error) => {
        console.error("Notification failed:", error);
      });
  };

  return (
    <ProjectContext.Provider
      value={{
        addMilestoneModalOpen,
        canProjectBeCompleted,
        canUserModifyProject,
        canUser,
        closeMilestoneModal,
        ctqTestPlans,
        ctqTestPlansLoading,
        ctqTestPlansSuccess,
        currentSchedule,
        deleteMilestone,
        editMilestone,
        expandedMilestone,
        isProjectComplete,
        isUserProjectLeader,
        manageSchedulesModalOpen,
        milestoneListCollapsed,
        milestoneListChildren,
        milestoneStatusActionCommentModal,
        milestoneStatusChange,
        updatingMilestoneStatus,
        openMilestoneModal,
        requestCtqTestPlans,
        setCurrentSchedule,
        setEditMilestone,
        setExpandedMilestone,
        toggleTopOnly,
        addTopLevelMilestoneToShowOrHideChildren,
        removeTopLevelMilestoneFromShowOrHideChildren,
        setManageSchedulesModalOpen,
        setMilestoneListCollapsed,
        setMilestoneStatusActionCommentModal,
        updateMilestone,
        updatingMilestone,
        project,
        setProject,
        refetchProject,
        projectLoading,
        templateRolesMap,
        templateRolesMapLoading,
        refetchTemplateRolesMapping,
        handleNotifyApprovers,
      }}
    >
      {project ? children : <Spin />}
      {addMilestoneModalOpen && (
        <AddMilestoneFromListModal
          isOpen={addMilestoneModalOpen}
          onCancel={closeMilestoneModal}
          parentId={parentMilestone}
        />
      )}
      {manageSchedulesModalOpen && (
        <ManageProjectScheduleModal
          isOpen={manageSchedulesModalOpen}
          onCancel={() => setManageSchedulesModalOpen(false)}
        />
      )}
    </ProjectContext.Provider>
  );
};

ProjectProvider.propTypes = {
  children: node.isRequired,
};
